import { Skeleton } from "@xola/ui-kit";
import Highcharts, { SeriesOptionsType } from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { merge } from "lodash";
import React, { useCallback } from "react";
import { useQuery, useQueryClient } from "react-query";
import { Product } from "types";
import shallow from "zustand/shallow";
import { useSeller } from "../../../hooks/useAccount";
import { useAutoFetchCollection } from "../../../hooks/useAutoFetchCollection";
import { DemographicsReportParams, getDemographicsReport } from "../../../reports/DemographicsReport";
import { getProductReport, ProductsReportParams } from "../../../reports/ProductsReport";
import { useCustomerAnalyticsFilterStore } from "../../../stores/reports/customerAnalyticsFilterStore";
import { PieOptions } from "../Analytics/Base/PieOptions";
import { api } from "@/api/api";

export const DemographicsPie = () => {
    return <PieChart title="Demographics" prefix="demographics" fetch={getDemographicsReport} />;
};

export const ProductsPie = () => {
    const getName = useExperienceName();
    const { isLoading } = useAutoFetchCollection(["products"]);

    if (isLoading) {
        // We need to wait for products to load so that the pie chart can show the legend properly
        return <Skeleton height={200} classNames={{ container: "mx-2" }} />;
    }

    return <PieChart title="Products" prefix="products" fetch={getProductReport} getName={getName} />;
};

const useExperienceName = () => {
    const queryClient = useQueryClient();

    return useCallback(
        async (id: ID): Promise<string> => {
            try {
                const cachedProduct = queryClient.getQueryData<Product>(["products", id]);
                if (cachedProduct) {
                    return cachedProduct.name;
                }

                const product: Product = await queryClient.fetchQuery(["products", id], async () => {
                    const { data } = await api.get<Product>(`/products/${id}`);
                    return data;
                });

                return product.name ?? "";
            } catch (error) {
                console.error("Failed to fetch product name:", error);
                return id;
            }
        },
        [queryClient],
    );
};

interface PieChartProps {
    title: string;
    prefix: string;
    params?: { terms: string; stats: string };
    getName?: (key: any) => string | Promise<string>;
    fetch: (params: DemographicsReportParams | ProductsReportParams) => Promise<SeriesOptionsType[]>;
}

const PieChart = ({ title, prefix, params, getName, fetch }: PieChartProps) => {
    const seller = useSeller();

    const [from, to] = useCustomerAnalyticsFilterStore((state) => [state.getFrom(), state.getTo()], shallow);
    const filters = useCustomerAnalyticsFilterStore((state) => state.filters);
    const products: ID[] = filters.products.all ? [] : filters.products.selected ?? [];

    const finalParams = { ...params, seller, mode: filters.mode, products, from, to, getName };
    const { data, isLoading } = useQuery<SeriesOptionsType[]>(["pieChart", prefix, finalParams], async () =>
        fetch(finalParams),
    );

    if (isLoading) {
        return <Skeleton height={200} classNames={{ container: "mx-2" }} />;
    }

    return (
        <div>
            {/* Div is required to wrap the SVG for rendering height properly */}
            <HighchartsReact highcharts={Highcharts} options={getChartOptions(title, data)} />
        </div>
    );
};

const getChartOptions = (title: string, data: any): Highcharts.Options => {
    const options: Highcharts.Options = {
        legend: { className: "no-translate" },
        series: [{ type: "pie", data }],
        title: { text: title, align: "left" },
    };
    return merge({}, PieOptions, options);
};
